import { STATUS_LABELS, type Resolution, type TicketStatus } from '@lixionary-jira/domain';
import type { ReactNode } from 'react';

export type Tone = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';

export function Badge({
  tone = 'default',
  dot = true,
  children,
  title,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`badge badge-${tone}`} title={title}>
      {dot && <span className="dot" aria-hidden />}
      {children}
    </span>
  );
}

const STATUS_TONE: Record<TicketStatus, Tone> = {
  pending: 'default',
  todo: 'info',
  in_progress: 'primary',
  pending_release: 'warning',
  closed: 'success',
};

/** Status text is always visible; color is never the only signal (NFR-06). */
export function StatusBadge({
  status,
  resolution,
}: {
  status: TicketStatus;
  resolution?: Resolution | null;
}) {
  if (status === 'closed') {
    return (
      <Badge
        tone={resolution === 'rejected' ? 'default' : 'success'}
      >{`Closed · ${resolution === 'rejected' ? 'Rejected' : 'Done'}`}</Badge>
    );
  }
  return <Badge tone={STATUS_TONE[status]}>{STATUS_LABELS[status]}</Badge>;
}

const STATE_TONES: Record<string, Tone> = {
  pending: 'warning',
  approved: 'success',
  denied: 'error',
  revoked: 'default',
  expired: 'default',
  stale: 'default',
  draft: 'default',
  awaiting_approval: 'warning',
  deploying: 'info',
  unknown: 'warning',
  failed: 'error',
  succeeded: 'success',
  cancelled: 'default',
  active: 'success',
  ready: 'success',
  released: 'default',
  recovered: 'default',
  terminated: 'default',
  clean: 'success',
  quarantined: 'error',
  removed: 'default',
  redeemed: 'success',
  reported: 'info',
  verified: 'success',
  inactive: 'default',
};

export const humanize = (s: string) => {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export function StateBadge({ state, label }: { state: string; label?: string }) {
  const tone = state === 'stale' && label === 'Stale owner' ? 'error' : (STATE_TONES[state] ?? 'default');
  return <Badge tone={tone}>{label ?? humanize(state)}</Badge>;
}
